import {useMemo, useState} from 'react';

import {Box, Collapse, Typography} from '@mui/material';

import {TargetProfileSkillAutocomplete} from './TargetProfileSkillAutocomplete';
import {TargetProfileSkillChipGroup} from './TargetProfileSkillChipGroup';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import type {StandardSkill} from '../../../profile/types';
import type {SkillCategory, TargetProfileSkill} from '../../types/DepartmentTargetProfileTypes';

export type RequirementLevel = 'must_have' | 'nice_to_have';

const MUST_HAVE: RequirementLevel = 'must_have';
const NICE_TO_HAVE: RequirementLevel = 'nice_to_have';

const groupSkillsByRequirement = (skills: TargetProfileSkill[]) => ({
  mustHave: skills.filter((skill) => skill.requirementLevel === MUST_HAVE),
  niceToHave: skills.filter((skill) => skill.requirementLevel === NICE_TO_HAVE),
});

export interface Props {
  label: string;
  skills: TargetProfileSkill[];
  language: keyof TargetProfileSkill['name'];
  mustHaveLabel: string;
  niceToHaveLabel: string;
  excludedSkillIds?: ReadonlySet<number>;
  onToggleSkillExclusion?: (standardSkillId: number) => void;
  onRemoveSkill?: (standardSkillId: number) => void;
  onToggleRequirementLevel?: (skill: TargetProfileSkill) => void;
  onAddSkill?: (skill: StandardSkill, requirementLevel: RequirementLevel) => void;
  onAddCustomSkill?: (name: string, requirementLevel: RequirementLevel) => void;
  skillCategory?: SkillCategory;
  occupationResourceId?: string;
  excludeSkillLabel?: string;
  includeSkillLabel?: string;
  removeSkillLabel?: string;
  toggleRequirementLabel?: string;
  addSkillLabel?: string;
  cancelAddSkillLabel?: string;
  createSkillLabel?: (name: string) => string;
  disabled?: boolean;
  collapseAddControlsByDefault?: boolean;
  hideLabel?: boolean;
}

export const TargetProfileSkillSection = ({
  label,
  skills,
  language,
  mustHaveLabel,
  niceToHaveLabel,
  excludedSkillIds,
  onToggleSkillExclusion,
  onRemoveSkill,
  onToggleRequirementLevel,
  onAddSkill,
  onAddCustomSkill,
  skillCategory,
  occupationResourceId,
  excludeSkillLabel,
  includeSkillLabel,
  removeSkillLabel,
  toggleRequirementLabel,
  addSkillLabel,
  cancelAddSkillLabel,
  createSkillLabel,
  disabled,
  collapseAddControlsByDefault = false,
  hideLabel = false,
}: Props) => {
  const {mustHave, niceToHave} = useMemo(() => groupSkillsByRequirement(skills), [skills]);
  const isCurationMode = Boolean(onRemoveSkill || onAddSkill || onAddCustomSkill);
  const [addControlsOpen, setAddControlsOpen] = useState(false);
  const existingNames = useMemo(() => {
    const names = new Set<string>();
    skills.forEach((skill) => {
      const name = skill.name[language]?.toLowerCase();
      if (name) {
        names.add(name);
      }
    });
    return names;
  }, [skills, language]);

  if (!mustHave.length && !niceToHave.length && !isCurationMode) {
    return null;
  }

  const chipGroupProps = {
    language,
    excludedSkillIds,
    onToggleSkillExclusion,
    onRemoveSkill,
    onToggleRequirementLevel,
    excludeSkillLabel,
    includeSkillLabel,
    removeSkillLabel,
    toggleRequirementLabel,
  };

  const canAddSkills =
    Boolean(onAddSkill) &&
    Boolean(onAddCustomSkill) &&
    Boolean(addSkillLabel) &&
    Boolean(createSkillLabel) &&
    Boolean(skillCategory);

  const renderInlineAutocomplete = (requirementLevel: RequirementLevel) => {
    if (!canAddSkills || !onAddSkill || !onAddCustomSkill || !addSkillLabel || !createSkillLabel || !skillCategory) {
      return null;
    }

    return (
      <TargetProfileSkillAutocomplete
        label={addSkillLabel}
        createLabel={createSkillLabel}
        skillCategory={skillCategory}
        occupationResourceId={occupationResourceId}
        disabled={disabled}
        excludeSkillNames={existingNames}
        onSelectExisting={(skill) => onAddSkill(skill, requirementLevel)}
        onSelectCustom={(name) => onAddCustomSkill(name, requirementLevel)}
      />
    );
  };

  const renderRequirementBlock = (
    requirementLevel: RequirementLevel,
    groupLabel: string,
    groupSkills: TargetProfileSkill[],
    variant: 'mustHave' | 'niceToHave',
    includeInlineAdd: boolean
  ) => (
    <Box sx={{mb: 1}}>
      <TargetProfileSkillChipGroup
        label={groupLabel}
        skills={groupSkills}
        variant={variant}
        showLabelWhenEmpty={canAddSkills}
        {...chipGroupProps}
      />
      {includeInlineAdd && renderInlineAutocomplete(requirementLevel)}
    </Box>
  );

  return (
    <Box sx={{mb: hideLabel ? 0 : 1.5}}>
      {!hideLabel && (
        <Typography variant='caption' sx={{color: greenColors[700], fontWeight: 600, display: 'block', mb: 0.75}}>
          {label}
        </Typography>
      )}
      {renderRequirementBlock(
        MUST_HAVE,
        mustHaveLabel,
        mustHave,
        'mustHave',
        canAddSkills && !collapseAddControlsByDefault
      )}
      {renderRequirementBlock(
        NICE_TO_HAVE,
        niceToHaveLabel,
        niceToHave,
        'niceToHave',
        canAddSkills && !collapseAddControlsByDefault
      )}
      {canAddSkills && collapseAddControlsByDefault && (
        <Box sx={{mt: 0.5}}>
          <EmpionButton
            variant='secondary'
            size='small'
            disabled={disabled}
            onClick={() => setAddControlsOpen((open) => !open)}
          >
            {addControlsOpen && cancelAddSkillLabel ? cancelAddSkillLabel : addSkillLabel}
          </EmpionButton>
          <Collapse in={addControlsOpen}>
            <Box sx={{mt: 1}}>
              <Box sx={{mb: 1}}>
                <Typography variant='body2' sx={{color: greenColors[700], fontWeight: 600, mb: 0.5}}>
                  {mustHaveLabel}
                </Typography>
                {renderInlineAutocomplete(MUST_HAVE)}
              </Box>
              <Box sx={{mb: 1}}>
                <Typography variant='body2' sx={{color: greenColors[700], fontWeight: 600, mb: 0.5}}>
                  {niceToHaveLabel}
                </Typography>
                {renderInlineAutocomplete(NICE_TO_HAVE)}
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
